import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useTheme, Box } from "@mui/material"
import _ from "lodash"

import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useGeoLocation } from "providers/geolocation"

import { convertMapViewObjectToPath } from "utils/map"
import { plural as pluralize } from "pluralize"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { SearchToggleButton } from "components/domains/search"
import { MyLocationButton } from "components/Button"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { KioskOrdinalSelector } from "extensions/iconsiam/components/ordinal-selector"
import AttractionButtons from "extensions/iconsiam/components/domains/attractions/AttractionButton"
import { Q_VENUE } from "constant"
import { FeatureToggleChipTabBar } from "components/domains/features"
import { TouristRegistrationPanel } from "extensions/iconsiam/components/domains/TouristRegistrationPanel"
import { SideBannerPanel } from "extensions/iconsiam/components/domains/SideBannerPanel"

export const MapOrdinalKioskVPage = ({
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get Users or Devices Location(s)
  const { userLocation } = useGeoLocation()

  // Get ordinal from url
  const { ordinal } = useParams()

  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const {
    viewingVenueId,
    viewingVenueView,
    quickSearchCategories,
    locationVenueId,
    locationOrdinal,
    sortedLevels,
  } = useMap()

  const parsedOrdinal = Number(ordinal)

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView || userLocationView || viewingVenueView)

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    actionButton,
    actionContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    navigationWrapper,
    locationButtonContainer,
    ordinalSelector,
    searchBarContainer,
    attractionButton,
    touristRegistrationPanel,
    sideBannerPanel,
  } = styleOverrides

  const handleOrdinalChange = useCallback(
    ({ ordinal, venueId }) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)

      const ordinalPath = ordinal !== null ? `ordinal/${ordinal}` : ""
      const search = searchParams.size > 0 ? `?${searchParams.toString()}` : ""

      navigate(`/maps/${ordinalPath}${search}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleClickQuickSearch = useCallback(
    (type, category) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(type)
      navigate(
        `/maps/${pluralType}/${category}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  return (
    <>
      {sortedLevels.length > 1 && (
        <Box sx={ordinalSelector}>
          <KioskOrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={sortedLevels}
            onChangeOrdinal={handleOrdinalChange}
            currentVenue={locationVenueId}
            viewingVenueId={viewingVenueId}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}

      {userLocation && (
        <Box sx={locationButtonContainer}>
          <MyLocationButton onClick={handleClickMyLocation} />
        </Box>
      )}

      <TouristRegistrationPanel sx={touristRegistrationPanel} />

      <SideBannerPanel sx={sideBannerPanel} />

      <Box sx={attractionButton}>
        <AttractionButtons />
      </Box>

      <Box key="app-navigation" sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureToggleChipTabBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <Box sx={toolbarContainer}>
          <Box sx={actionContainer}>
            <LanguageButton sx={actionButton} />
            {additionalMenu}
          </Box>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search shops and more...")}
              onClick={openSearch}
            />
          </Box>
        </Box>
        {AppNavigation && <AppNavigation />}
      </Box>
    </>
  )
}
